/**
 * File Node Renderer
 *
 * Renders file nodes with an optional thumbnail preview and file icon badge.
 * When a thumbnail is available (e.g. rendered first page of a DOCX), it is
 * shown as the main visual with the filename + type badge below.
 * When no thumbnail is available, falls back to a centered icon + filename card.
 *
 * Node data:
 * - fileName: display name of the file
 * - fileType: MIME type or extension hint
 * - pageImageUrl: optional thumbnail blob/data URL (set by app, like PDF nodes)
 * - src: uploaded media URL (for AI/retrieval)
 */

import type { UINode, ScreenMetrics } from '../types';
import type {
	NodeRenderer,
	RenderLayers,
	RenderResult,
	LODLevel,
} from '../renderer';
import { calculateLOD } from '../renderer';
import { hashContent } from '../texture-cache';

/** File node data interface */
export interface FileNodeData {
	/** Display filename */
	fileName: string;
	/** MIME type or file extension hint */
	fileType?: string;
	/** Thumbnail image URL (blob/data URL of rendered first page) */
	pageImageUrl?: string;
	/** Uploaded media URL (for AI/retrieval) */
	src?: string;
}

/** Map of file extensions / MIME fragments to display info */
const FILE_ICONS: Record<string, { label: string; color: string }> = {
	docx: { label: 'DOCX', color: '#2b579a' },
	doc: { label: 'DOC', color: '#2b579a' },
	xlsx: { label: 'XLSX', color: '#217346' },
	xls: { label: 'XLS', color: '#217346' },
	pptx: { label: 'PPTX', color: '#d24726' },
	ppt: { label: 'PPT', color: '#d24726' },
	txt: { label: 'TXT', color: '#666' },
	csv: { label: 'CSV', color: '#217346' },
	zip: { label: 'ZIP', color: '#f0a030' },
};

/**
 * Determine the file icon info from filename and MIME type.
 */
function getFileIcon(
	fileName: string,
	fileType?: string,
): { label: string; color: string } {
	// Try extension first
	const ext = fileName.split('.').pop()?.toLowerCase();
	if (ext && FILE_ICONS[ext]) return FILE_ICONS[ext];

	// Try MIME type fragments
	if (fileType) {
		if (fileType.includes('wordprocessing') || fileType.includes('msword'))
			return FILE_ICONS.docx;
		if (fileType.includes('spreadsheet') || fileType.includes('excel'))
			return FILE_ICONS.xlsx;
		if (fileType.includes('presentation') || fileType.includes('powerpoint'))
			return FILE_ICONS.pptx;
		if (fileType.includes('text/plain')) return FILE_ICONS.txt;
		if (fileType.includes('text/csv')) return FILE_ICONS.csv;
		if (fileType.includes('zip')) return FILE_ICONS.zip;
	}

	return { label: ext?.toUpperCase() || 'FILE', color: '#888' };
}

/**
 * File renderer — displays a thumbnail preview (when available) or icon card.
 */
export class FileRenderer implements NodeRenderer {
	private lastLOD: Map<string, LODLevel> = new Map();
	private lastContent: Map<string, string> = new Map();
	private contentHashes: Map<string, string> = new Map();

	render(
		node: UINode,
		metrics: ScreenMetrics,
		layers: RenderLayers,
	): RenderResult {
		const opts = layers.options;

		const lod: LODLevel = opts.forceLod
			? opts.forceLod
			: opts.simplifyLOD
				? calculateLOD(metrics, {
						enableLodFull: opts.enableLodFull,
						enableLodTitle: opts.enableLodTitle,
						enableLodIcon: opts.enableLodIcon,
						enableLodDot: opts.enableLodDot,
					})
				: 'full';

		const data = this.getData(node);
		const contentHash = this.getContentHash(node, lod);
		const lastHash = this.contentHashes.get(node.id);
		const lastLod = this.lastLOD.get(node.id);
		const lodChanged = lastLod !== lod;
		const isNewElement = layers.html.isNewlyCreated(node.id);

		layers.html.setDataAttribute(node.id, 'lod', lod);

		if (!isNewElement && !lodChanged && contentHash === lastHash) {
			return { lod, changed: false };
		}

		const content = this.renderContent(data, lod);
		layers.html.setContent(node.id, content);

		this.lastLOD.set(node.id, lod);
		this.contentHashes.set(node.id, contentHash);
		this.lastContent.set(node.id, content);

		return { lod, changed: true };
	}

	private getData(node: UINode): FileNodeData {
		const d = node.data as Record<string, unknown>;
		const obj = (d.obj as Record<string, unknown>) || d;
		return {
			fileName: (obj.fileName as string) || (obj.title as string) || 'File',
			fileType: (obj.fileType as string) || undefined,
			pageImageUrl: (d.pageImageUrl as string) || undefined,
			src: (obj.src as string) || (obj.sourceFile as string) || undefined,
		};
	}

	private getContentHash(node: UINode, lod: LODLevel): string {
		const d = this.getData(node);
		return hashContent(
			`file:${lod}:${d.fileName}:${d.fileType || ''}:${d.pageImageUrl || ''}`,
		);
	}

	private renderContent(data: FileNodeData, lod: LODLevel): string {
		switch (lod) {
			case 'full':
				return this.renderFull(data);
			case 'title':
				return this.renderTitle(data);
			case 'icon':
				return this.renderIcon(data);
			case 'dot':
				return `<div class="file-node file-dot"></div>`;
		}
	}

	private renderFull(data: FileNodeData): string {
		const { fileName, fileType, pageImageUrl } = data;
		const icon = getFileIcon(fileName, fileType);

		if (pageImageUrl) {
			// Thumbnail mode: image preview with filename + badge below
			return `
        <div class="node-close-btn" data-action="close">&times;</div>
        <div class="file-node file-full file-has-thumb">
          <img
            src="${this.escapeHtml(pageImageUrl)}"
            alt="${this.escapeHtml(fileName)}"
            class="file-thumb-image"
            draggable="false"
          />
          <div class="file-info-bar">
            <span class="file-badge" style="background: ${icon.color}">${this.escapeHtml(icon.label)}</span>
            <span class="file-name">${this.escapeHtml(fileName)}</span>
          </div>
        </div>
      `;
		}

		// Fallback: icon card
		return `
      <div class="node-close-btn" data-action="close">&times;</div>
      <div class="file-node file-full">
        <div class="file-icon-box" style="background: ${icon.color}">
          <svg class="file-icon-svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
          </svg>
          <span class="file-icon-label">${this.escapeHtml(icon.label)}</span>
        </div>
        <div class="file-name">${this.escapeHtml(fileName)}</div>
      </div>
    `;
	}

	private renderTitle(data: FileNodeData): string {
		const { fileName, fileType, pageImageUrl } = data;
		const icon = getFileIcon(fileName, fileType);

		if (pageImageUrl) {
			return `
        <div class="file-node file-title-lod file-has-thumb">
          <img
            src="${this.escapeHtml(pageImageUrl)}"
            alt="${this.escapeHtml(fileName)}"
            class="file-thumb-image"
            draggable="false"
          />
          <div class="file-info-bar">
            <span class="file-badge" style="background: ${icon.color}">${this.escapeHtml(icon.label)}</span>
            <span class="file-name">${this.escapeHtml(fileName)}</span>
          </div>
        </div>
      `;
		}

		return `
      <div class="file-node file-title-lod">
        <div class="file-icon-box file-icon-box-sm" style="background: ${icon.color}">
          <span class="file-icon-label">${this.escapeHtml(icon.label)}</span>
        </div>
        <div class="file-name">${this.escapeHtml(fileName)}</div>
      </div>
    `;
	}

	private renderIcon(data: FileNodeData): string {
		const { fileName, fileType, pageImageUrl } = data;
		const icon = getFileIcon(fileName, fileType);

		if (pageImageUrl) {
			return `
        <div class="file-node file-icon-lod file-has-thumb">
          <img
            src="${this.escapeHtml(pageImageUrl)}"
            alt="${this.escapeHtml(fileName)}"
            class="file-thumb-image"
            draggable="false"
          />
        </div>
      `;
		}

		return `
      <div class="file-node file-icon-lod" style="background: ${icon.color}">
        <span class="file-icon-label">${this.escapeHtml(icon.label)}</span>
      </div>
    `;
	}

	private escapeHtml(text: string): string {
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	getPreferredSize(node: UINode): { width: number; height: number } {
		return {
			width: node.size?.width ?? 300,
			height: node.size?.height ?? 400,
		};
	}

	cleanup(nodeId: string): void {
		this.lastLOD.delete(nodeId);
		this.lastContent.delete(nodeId);
		this.contentHashes.delete(nodeId);
	}
}
